/* ==========================================================================
   Gift frame — automatic slot placement
   --------------------------------------------------------------------------
   Every frame artwork has the letter text baked in, in a different position.
   Shared default coordinates therefore drop the photo and the signature on top
   of that text. This finds the empty areas instead.

   How it works
   ------------
   1. Draw the artwork into a small offscreen canvas (160px wide is plenty).
   2. Sample the four corners to learn the background colour.
   3. Mark every cell that differs from the background as "content".
   4. Find the largest all-empty rectangle in the upper part of the image for
      the photo, and the largest empty band in the lower part for the
      signature, using the classic largest-rectangle-in-histogram scan.

   Product metafields always win. This only fills in what the merchant has not
   set explicitly, so tuning a specific frame by hand still overrides it.

   Requires an untainted canvas, so the artwork is loaded with crossOrigin.
   If that fails the module returns null and the caller keeps its defaults.
   ========================================================================== */

window.GiftAutoPlace = (function () {
  'use strict';

  var GRID_W = 160;
  var TOLERANCE = 26; // summed RGB distance before a pixel counts as content

  function buildGrid(art) {
    var ratio = (art.naturalHeight || 1) / (art.naturalWidth || 1);
    var w = GRID_W;
    var h = Math.max(8, Math.round(GRID_W * ratio));

    var canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;

    var ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(art, 0, 0, w, h);

    var data;
    try {
      data = ctx.getImageData(0, 0, w, h).data;
    } catch (err) {
      return null; // canvas tainted — no CORS headers on the artwork
    }

    function at(x, y) {
      var i = (y * w + x) * 4;
      return [data[i], data[i + 1], data[i + 2]];
    }

    // Median of the corners, so one dark corner cannot skew the background.
    var corners = [at(0, 0), at(w - 1, 0), at(0, h - 1), at(w - 1, h - 1)];
    var bg = [0, 1, 2].map(function (channel) {
      var values = corners
        .map(function (c) { return c[channel]; })
        .sort(function (a, b) { return a - b; });
      return (values[1] + values[2]) / 2;
    });

    var grid = new Uint8Array(w * h);
    for (var y = 0; y < h; y++) {
      for (var x = 0; x < w; x++) {
        var p = at(x, y);
        var diff =
          Math.abs(p[0] - bg[0]) + Math.abs(p[1] - bg[1]) + Math.abs(p[2] - bg[2]);
        grid[y * w + x] = diff > TOLERANCE ? 1 : 0;
      }
    }

    return { grid: grid, w: w, h: h };
  }

  /* Largest all-empty rectangle within rows [top, bottom). */
  function largestEmptyRect(model, top, bottom) {
    var w = model.w;
    var heights = new Int32Array(w);
    var best = null;

    for (var y = top; y < bottom; y++) {
      var limit = y - top + 1;

      for (var x = 0; x < w; x++) {
        heights[x] = model.grid[y * w + x] ? 0 : heights[x] + 1;
        if (heights[x] > limit) heights[x] = limit;
      }

      var stack = [];
      for (var i = 0; i <= w; i++) {
        var height = i === w ? 0 : heights[i];
        var start = i;

        while (stack.length && stack[stack.length - 1].h >= height) {
          var top_ = stack.pop();
          var area = top_.h * (i - top_.x);

          if (!best || area > best.area) {
            best = {
              area: area,
              x: top_.x,
              y: y - top_.h + 1,
              w: i - top_.x,
              h: top_.h
            };
          }

          start = top_.x;
        }

        stack.push({ x: start, h: height });
      }
    }

    return best;
  }

  function analyse(art) {
    var model = buildGrid(art);
    if (!model) return null;

    var result = {};

    /* --- Photo: biggest clear square in the top 60% ---------------------- */

    var upper = largestEmptyRect(model, 0, Math.round(model.h * 0.6));

    if (upper && upper.w > 6 && upper.h > 6) {
      // Keep it square-ish and leave breathing room inside the clear area.
      var side = Math.min(upper.w, upper.h) * 0.82;
      side = Math.min(side, model.w * 0.42);

      result.photo = {
        x: ((upper.x + (upper.w - side) / 2) / model.w) * 100,
        y: ((upper.y + (upper.h - side) / 2) / model.h) * 100,
        w: (side / model.w) * 100,
        h: (side / model.h) * 100
      };
    }

    /* --- Signature: widest clear band in the bottom 45% ------------------ */

    var lower = largestEmptyRect(model, Math.round(model.h * 0.55), model.h);

    if (lower && lower.w > model.w * 0.35 && lower.h > 2) {
      result.name = {
        x: ((lower.x + lower.w / 2) / model.w) * 100,
        y: ((lower.y + lower.h / 2) / model.h) * 100,
        // Comfortably inside the band, and never larger than a sane cap.
        size: Math.min(((lower.h * 0.45) / model.w) * 100, 5)
      };
    }

    return result;
  }

  /* Loads the artwork in a CORS-safe way so the pixels can be read. */
  function fromUrl(url) {
    return new Promise(function (resolve) {
      if (!url) {
        resolve(null);
        return;
      }

      var art = new Image();
      art.crossOrigin = 'anonymous';
      art.onload = function () {
        try {
          resolve(analyse(art));
        } catch (err) {
          resolve(null);
        }
      };
      art.onerror = function () {
        resolve(null);
      };
      art.src = url;
    });
  }

  return { fromUrl: fromUrl, analyse: analyse };
})();
